/**
 * Profile Route Definition (Non-Lazy)
 *
 * Loader only. The component tree lives in index.lazy.tsx so its imports
 * (lucide icons, nav cards, boneyard fixtures) stay out of the entry chunk —
 * a non-lazy component here hoisted all of them into the eager boot graph.
 */
import { createFileRoute } from '@tanstack/react-router';
import { ProfileHubTemplatePending } from '@/components/v2/page-template-pending';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import type { PrivateProfile } from '@/lib/api/v2/profile';

export const Route = createFileRoute('/_authenticated/profile/')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The hub is avatar-header + nav cards, NOT a back-header list, so it gets
  // its own template.
  pendingComponent: ProfileHubTemplatePending,
  loader: ({ context }) => {
    // ensureQueryData (not a bare fetch): intent preloads and the committed
    // navigation share one cache entry, and a fresh ['profile'] — e.g. right
    // after a save — resolves without touching the network. On client-side
    // navigations the fetch goes browser → Go API directly (one hop); only
    // SSR pays the cookie-forwarding proxy hop.
    const refresh = () =>
      context.queryClient.ensureQueryData({
        queryKey: ['profile'],
        queryFn: () => authenticatedGetShared<PrivateProfile>('/v2/me/profile'),
      });
    // Cache-first gate: a cached ['profile'] resolves the loader instantly and
    // the background refresh heals staleness (single-flight-deduped with the
    // mounted query) — with defaultPendingMs at 0, a blocking loader would
    // swap every back-navigation to the destination skeleton. A cold cache
    // keeps the streamed fetch the Await boundary renders against.
    const { cached, stream } = cacheFirst(context.queryClient, ['profile'], refresh);
    return { profilePromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
});
